<script setup lang="ts">
import HasPermission from "@/components/HasPermission.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import ModalComponent from "@/components/ModalComponent.vue";
import ShadowBox from "@/components/ShadowBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledDatedMediaInput from "@/components/StyledDatedMediaInput.vue";
import useDateFormat from "@/composables/useDateFormat";
import { Hotel } from "@/interfaces/hotel";
import { VuePDF, usePDF } from "@tato30/vue-pdf";
import { reactive, ref, watch } from "vue";
import useHotelMedia from "../composables/useHotelMedia";

interface Props {
  hotel: Hotel;
}

const props = defineProps<Props>();

interface Form {
  file: File | null;
  expiresAt: string;
}

const form = reactive<Form>({
  file: null,
  expiresAt: "",
});

const { createMediaHotelAsyncMutation, data, isLoading, refetch } =
  useHotelMedia({
    immediate: true,
    params: {
      id: props.hotel.id,
    },
  });

const handleSave = async (
  value: "id" | "passport" | "driverLicense",
  file: File,
  expiresAt: string,
) => {
  await createMediaHotelAsyncMutation({
    id: props.hotel.id,
    collection: "agreement",
    media: file,
    expirationDate: expiresAt,
  });

  form.file = null;
  form.expiresAt = "";

  await refetch();
  open.value = false;
};

const file = ref<string | null>("");

watch(
  () => data.value,
  (value) => {
    if (value) {
      file.value = value[0]?.originalUrl;
    }
  },
);

const scale = ref(1);
const page = ref(1);
const { pdf, pages } = usePDF(file);

const open = ref(false);

const zoomOut = () => {
  if (scale.value > 1) {
    scale.value = scale.value - 0.25;
  }
};

const zoomIn = () => {
  if (scale.value < 1.5) {
    scale.value = scale.value + 0.25;
  }
};
</script>

<template>
  <ShadowBox class="bg-white">
    <div class="gap-4">
      <div class="grid grid-cols-2">
        <div class="flex flex-col gap-2" v-if="data?.length">
          <h2 class="font-bold text-lg text-left">
            Fecha de vencimiento:
            {{ data ? useDateFormat(data[0]?.expirationDate).date : "" }}
          </h2>

          <div class="flex items-center gap-2">
            <button
              @click="zoomOut"
              class="text-blue-500 material-symbols-outlined text-3xl"
            >
              zoom_out
            </button>
            <span class="font-bold text-xl">{{ scale * 100 }}%</span>
            <button
              @click="zoomIn"
              class="text-blue-500 material-symbols-outlined text-3xl"
            >
              zoom_in
            </button>
          </div>
        </div>

        <HasPermission
          :entity-type="'App\\Models\\Hotel'"
          :name-permission="'create-medias-internal'"
        >
          <div>
            <StyledButton
              class="bg-[#253C70] text-white ml-auto"
              @click="
                () => {
                  open = !open;
                }
              "
            >
              Cargar nuevo convenio
            </StyledButton>
          </div>
        </HasPermission>
      </div>

      <div class="flex flex-col items-center" v-if="data">
        <div>
          <VuePDF :pdf="pdf" :page="page" :scale="scale" class="" />
        </div>

        <div class="flex gap-2 items-center" v-if="pdf">
          <StyledButton
            class="bg-[#253C70] text-white"
            @click="page--"
            :disabled="Boolean(pages && page <= 1)"
          >
            <span class="material-symbols-outlined"> chevron_left </span>
          </StyledButton>

          <p>{{ page }} / {{ pages }}</p>

          <StyledButton
            class="bg-[#253C70] text-white"
            @click="page++"
            :disabled="Boolean(pages && page >= pages)"
          >
            <span class="material-symbols-outlined"> chevron_right </span>
          </StyledButton>
        </div>
      </div>
    </div>
  </ShadowBox>

  <ModalComponent v-model="open">
    <template #modalBody>
      <div class="p-5">
        <h2 class="text-xl font-medium">Convenio</h2>

        <div class="grid grid-cols-1 gap-6">
          <StyledDatedMediaInput
            media-key="id"
            v-model:file="form.file"
            v-model:expiresAt="form.expiresAt"
            :accept-types="['application/pdf']"
            :max-kb="20480"
            :name-permission-create="''"
            :name-permission-update="''"
            :name-permission-delete="''"
            :entity-type="''"
            :save-media="() => handleSave('id', form.file!, form.expiresAt)"
          />
        </div>
      </div>
    </template>
  </ModalComponent>

  <LoadingModal :when="isLoading" />
</template>
